import { Box, Fab, Portal, Button, ButtonGroup, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { Collaborator, getCollaboratorsByCompany } from "src/apis/company";
import PageContainer from "src/pages/components/PageContainer";
import { getUserCompanyId, isUserAdministrator, isUserCompany } from "src/utils/auth";
import { Id } from "src/utils/types";
import CollaboratorsTable from "./components/CollaboratorsTable";
import CompanyCollaboratorsTable from "./components/CompanyCollaboratorsTable";
import CompanySelect from "src/pages/components/CompanySelect";
import FilterButtons from "src/pages/components/SearchAndResetButtons";
import RegisterFab from "src/pages/components/RegisterFab";
import { CloudUpload } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useCompanyBlocks } from "src/context/CompanyBlocksContext";

type BlockFilter = "todos" | "bloqueados" | "desbloqueados";

const Collaborators: FC = (): JSX.Element => {
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [companyId, setCompanyId] = useState<Id>('');
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [sortBy, setSortBy] = useState<"id" | "name">("id");
  const [blockFilter, setBlockFilter] = useState<BlockFilter>("todos");

  const isAdmin = useMemo<boolean>(isUserAdministrator, []);
  const isCompany = useMemo<boolean>(() => isUserCompany() && !isUserAdministrator(), []);

  const navigate = useNavigate();
  const { isActionRestricted } = useCompanyBlocks();
  const isInviteBlocked = isActionRestricted("invite_collaborator");

  // ID fixo da empresa Eezyfamily
  const DEFAULT_TARGET_COMPANY_ID = 45;

  const effectiveCompanyId: Id = useMemo(
    () => (isAdmin ? companyId : getUserCompanyId()),
    [isAdmin, companyId]
  );

  const fetchCollaborators = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const id = effectiveCompanyId;
      if (!id) {
        setError("Nenhum ID de empresa disponível.");
        setCollaborators([]);
        return;
      }
      const res = await getCollaboratorsByCompany(id);
      setCollaborators(res);
      setError('');
    } catch (err: any) {
      setError(err?.message || "Erro ao buscar colaboradores.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) {
      // empresa comum carrega automaticamente
      fetchCollaborators();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sortedCollaborators = useMemo(() => {
    const arr = [...collaborators];
    if (sortBy === "id") {
      return arr.sort((a, b) => (a.id_colaborador ?? 0) - (b.id_colaborador ?? 0));
    }
    return arr.sort((a, b) =>
      (a.nome ?? "").localeCompare(b.nome ?? "", "pt-BR", { sensitivity: "base" })
    );
  }, [collaborators, sortBy]);

  const filteredCollaborators = useMemo(() => {
    if (blockFilter === "todos") return sortedCollaborators;
    const wantBlocked = blockFilter === "bloqueados";
    return sortedCollaborators.filter((c) => Boolean(c.bloqueio) === wantBlocked);
  }, [sortedCollaborators, blockFilter]);

  return (
    <PageContainer title="Colaboradores" error={error}>
      {(isAdmin || isCompany) && (
        <Portal>
          <Fab
            color="primary"
            variant="extended"
            disabled={isCompany && isInviteBlocked}
            title={isCompany && isInviteBlocked ? "Cadastro de colaboradores temporariamente suspenso para a empresa." : undefined}
            onClick={() => navigate("/colaboradores/importar")}
            sx={{
              margin: 0,
              marginBottom: 7,
              top: 'auto',
              right: 20,
              bottom: 20,
              left: 'auto',
              position: 'fixed',
              borderRadius: '5px',
              zIndex: (theme) => theme.zIndex.tooltip + 1,
            }}
          >
            <CloudUpload sx={{ marginRight: "0.25rem" }} />
            Importar Colaboradores
          </Fab>
        </Portal>
      )}

      {isAdmin && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", justifyContent: "center" }}>
          <CompanySelect isFilter companyId={companyId} setCompanyId={setCompanyId} setError={setError} />
          <FilterButtons
            isLoading={isLoading}
            registerUrl="/colaboradores/cadastro"
            search={fetchCollaborators}
            resetState={() => setCollaborators([])}
          />

          <ButtonGroup>
            <Button
              variant={sortBy === "id" ? "contained" : "outlined"}
              onClick={() => setSortBy("id")}
            >
              Ordenar por ID
            </Button>
            <Button
              variant={sortBy === "name" ? "contained" : "outlined"}
              onClick={() => setSortBy("name")}
            >
              Ordem alfabética
            </Button>
          </ButtonGroup>

          <ToggleButtonGroup
            exclusive
            color="primary"
            size="small"
            value={blockFilter}
            onChange={(_, value: BlockFilter | null) => value && setBlockFilter(value)}
          >
            <ToggleButton value="todos">Todos</ToggleButton>
            <ToggleButton value="bloqueados">Bloqueados</ToggleButton>
            <ToggleButton value="desbloqueados">Desbloqueados</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {isCompany && (
        <RegisterFab
          registerUrl="/colaboradores/cadastro"
          isDisabled={isInviteBlocked}
          disabledReason={isInviteBlocked ? "Cadastro de colaboradores temporariamente suspenso para a empresa." : undefined}
          label={isInviteBlocked ? "Cadastro bloqueado" : "Cadastrar"}
        />
      )}

      {/* Empresa usa tabela dedicada (sem prontuário/bloqueios, ação arquivar);
          Admin mantém a tabela completa. */}
      {isCompany ? (
        <CompanyCollaboratorsTable
          collaborators={sortedCollaborators}
          companyId={effectiveCompanyId}
          reloadList={fetchCollaborators}
          archiveTargetCompanyId={DEFAULT_TARGET_COMPANY_ID}  // 👈 ID fixo 45
        />
      ) : (
        <CollaboratorsTable
          collaborators={filteredCollaborators}
          companyId={effectiveCompanyId}
          reloadList={fetchCollaborators}
          defaultTargetCompanyId={DEFAULT_TARGET_COMPANY_ID}  // 👈 ID fixo 45
        />
      )}
    </PageContainer>
  );
};

export default Collaborators;
